import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, Users, CheckCircle, TrendingUp } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const expansionFeatures = [
  {
    icon: Users,
    title: "Operator Certification",
    description: "Licensed operators deploy authority under controlled standards, not improvisation.",
  },
  {
    icon: Globe,
    title: "Geographic Expansion",
    description: "International growth through certification, not headcount or operational sprawl.",
  },
  {
    icon: TrendingUp,
    title: "Vertical Replication",
    description: "Authority systems adapted for new verticals under the same governance framework.",
  },
];

const economicsData = [
  { label: "Certification Fees", margin: "~80%", type: "Upfront" },
  { label: "Annual Licensing", margin: "~90%", type: "Recurring" },
  { label: "Renewal & Compliance", margin: "~85%", type: "Recurring" },
];

const ExpansionPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="expansion-page">
      <SEOHead 
        title="Authority Systems - Licensing & Expansion"
        description="OnPoint Authority Systems expands through licensed operators, not headcount. Geographic and vertical growth is controlled through certification."
        url="https://onpointauthority.com/expansion"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-12" data-testid="expansion-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Scale Logic
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Licensing &<br />
              <span className="text-primary">Expansion</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              OnPoint Authority Systems expands through licensed operators, not headcount. 
              Geographic and vertical growth is controlled through certification.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Expansion Model */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="model-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Growth Model
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground">
              How We<br />
              <span className="text-primary">Scale</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {expansionFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/5 p-8"
                data-testid={`feature-${index}`}
              >
                <feature.icon className="h-8 w-8 text-primary mb-4" strokeWidth={1.5} />
                <h3 className="font-serif text-xl text-foreground mb-3">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Economics */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="economics-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Buyer-Grade Summary
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground">
              Operator Certification<br />
              <span className="text-primary">Economics</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {economicsData.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/5 p-6 text-center"
              >
                <p className="text-xs font-mono text-muted-foreground mb-2">{item.type}</p>
                <p className="text-sm text-foreground font-medium mb-1">{item.label}</p>
                <p className="text-2xl font-serif text-primary">{item.margin}</p>
                <p className="text-xs text-muted-foreground">Gross Margin</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="p-6 border border-primary/20 bg-primary/5"
          >
            <p className="text-sm text-foreground">
              <span className="text-primary font-medium">Buyer Insight:</span>{" "}
              Revenue scales with operator count—not founder time—creating clean multiple expansion.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Buyer Signal */}
      <section className="py-24 md:py-32 px-6 md:px-12 border-t border-white/5" data-testid="signal-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Buyer Signal
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-foreground mb-8">
              International Expansion<br />
              <span className="text-primary">Without Sprawl</span>
            </h2>
            <p className="text-lg font-light leading-relaxed text-muted-foreground mb-10">
              This model supports international expansion without operational sprawl. 
              Growth is governed by certification, not headcount.
            </p>
            <Link to="/authority-review">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow"
                data-testid="expansion-cta"
              >
                Request Authority Review
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ExpansionPage;
